/**
 * Google Analytics 4 Utility Functions
 * 
 * Bu dosya, Google Analytics olaylarını izlemek için yardımcı fonksiyonlar içerir.
 * Özel olaylar eklemek ve kullanıcı davranışlarını izlemek için kullanılabilir.
 */

/**
 * Check if gtag is available
 * @returns {boolean}
 */
export const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track custom event
 * @param {string} eventName - Event name (e.g., 'button_click')
 * @param {object} eventParams - Event parameters
 * 
 * @example
 * trackEvent('whatsapp_click', {
 *   event_category: 'Contact',
 *   event_label: 'WhatsApp Button',
 *   value: 1
 * });
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (isGtagAvailable()) {
    try {
      window.gtag('event', eventName, eventParams);
      console.log(`[GA4] Event tracked: ${eventName}`, eventParams);
    } catch (error) {
      console.error('[GA4] Error tracking event:', error);
    }
  } else {
    console.warn('[GA4] gtag is not available');
  }
};

/**
 * Track page view
 * @param {string} pageTitle - Page title
 * @param {string} pagePath - Page path (e.g., '/blog/apache-bugday')
 * 
 * @example
 * trackPageView('Blog - Apache Buğday', '/blog/apache-bugday');
 */
export const trackPageView = (pageTitle, pagePath) => {
  if (isGtagAvailable()) {
    try {
      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_path: pagePath,
        page_location: window.location.href
      });
      console.log(`[GA4] Page view tracked: ${pageTitle} - ${pagePath}`);
    } catch (error) {
      console.error('[GA4] Error tracking page view:', error);
    }
  }
};

/**
 * Track form submission
 * @param {string} formName - Form name (e.g., 'contact_form', 'quote_form')
 * @param {object} additionalParams - Additional parameters
 * 
 * @example
 * trackFormSubmit('contact_form', { form_location: 'header' });
 */
export const trackFormSubmit = (formName, additionalParams = {}) => {
  trackEvent('form_submit', {
    event_category: 'Form',
    event_label: formName,
    value: 1,
    ...additionalParams
  });
};

/**
 * Track WhatsApp button click
 * @param {string} location - Button location (e.g., 'floating', 'footer', 'header')
 * 
 * @example
 * trackWhatsAppClick('floating');
 */
export const trackWhatsAppClick = (location = 'unknown') => {
  trackEvent('whatsapp_click', {
    event_category: 'Contact',
    event_label: 'WhatsApp Button',
    button_location: location,
    value: 1
  });
};

/**
 * Track phone call button click
 * @param {string} location - Button location
 * 
 * @example
 * trackPhoneClick('header');
 */
export const trackPhoneClick = (location = 'unknown') => {
  trackEvent('phone_click', {
    event_category: 'Contact',
    event_label: 'Phone Button',
    button_location: location,
    value: 1
  });
};

/**
 * Track product view
 * @param {string} productName - Product name (e.g., 'Apache Makarnalık Buğday')
 * @param {string} productCategory - Product category (e.g., 'Buğday')
 * 
 * @example
 * trackProductView('Apache Makarnalık Buğday', 'Buğday');
 */
export const trackProductView = (productName, productCategory = '') => {
  trackEvent('view_item', {
    event_category: 'Product',
    event_label: productName,
    product_category: productCategory,
    value: 1
  });
};

/**
 * Track blog post read
 * @param {string} blogTitle - Blog post title
 * @param {string} blogCategory - Blog category
 * 
 * @example
 * trackBlogRead('Apache Buğday Ekim Rehberi', 'Ekim Rehberleri');
 */
export const trackBlogRead = (blogTitle, blogCategory = '') => {
  trackEvent('blog_read', {
    event_category: 'Blog',
    event_label: blogTitle,
    blog_category: blogCategory,
    value: 1
  });
};

/**
 * Track download
 * @param {string} fileName - Downloaded file name
 * @param {string} fileType - File type (e.g., 'pdf', 'doc')
 * 
 * @example
 * trackDownload('apache-tohum-katalogu.pdf', 'pdf');
 */
export const trackDownload = (fileName, fileType = '') => {
  trackEvent('file_download', {
    event_category: 'Download',
    event_label: fileName,
    file_type: fileType,
    value: 1
  });
};

/**
 * Track outbound link click
 * @param {string} url - External URL
 * @param {string} linkText - Link text
 * 
 * @example
 * trackOutboundLink('https://www.facebook.com/ortadoguziraat', 'Facebook');
 */
export const trackOutboundLink = (url, linkText = '') => {
  trackEvent('click', {
    event_category: 'Outbound Link',
    event_label: linkText || url,
    link_url: url,
    value: 1
  });
};

/**
 * Track search
 * @param {string} searchTerm - Search term
 * 
 * @example
 * trackSearch('apache buğday');
 */
export const trackSearch = (searchTerm) => {
  trackEvent('search', {
    event_category: 'Search',
    search_term: searchTerm,
    value: 1
  });
};

/**
 * Track video play
 * @param {string} videoTitle - Video title
 * @param {string} videoUrl - Video URL
 * 
 * @example
 * trackVideoPlay('Tohum Ekim Teknikleri', 'https://youtube.com/...');
 */
export const trackVideoPlay = (videoTitle, videoUrl = '') => {
  trackEvent('video_start', {
    event_category: 'Video',
    event_label: videoTitle,
    video_url: videoUrl,
    value: 1
  });
};

/**
 * Track scroll depth
 * @param {number} percentage - Scroll percentage (e.g., 25, 50, 75, 100)
 * 
 * @example
 * trackScrollDepth(75);
 */
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll', {
    event_category: 'Engagement',
    event_label: `${percentage}% Scrolled`,
    scroll_depth: percentage,
    value: percentage
  });
};

/**
 * Track time on page (call when user leaves or after certain duration)
 * @param {number} seconds - Time spent in seconds
 * @param {string} pagePath - Page path
 * 
 * @example
 * trackTimeOnPage(120, '/blog/apache-bugday');
 */
export const trackTimeOnPage = (seconds, pagePath) => {
  trackEvent('timing_complete', {
    event_category: 'Engagement',
    name: 'time_on_page',
    value: seconds,
    page_path: pagePath
  });
};

/**
 * Track error
 * @param {string} errorMessage - Error message
 * @param {string} errorLocation - Where the error occurred
 * 
 * @example
 * trackError('API request failed', 'contact_form');
 */
export const trackError = (errorMessage, errorLocation = '') => {
  trackEvent('exception', {
    description: errorMessage,
    fatal: false,
    error_location: errorLocation
  });
};

/**
 * Set user properties (for logged-in users if implemented)
 * @param {object} properties - User properties
 * 
 * @example
 * setUserProperties({ user_type: 'farmer', region: 'GAP' });
 */
export const setUserProperties = (properties) => {
  if (isGtagAvailable()) {
    try {
      window.gtag('set', 'user_properties', properties);
      console.log('[GA4] User properties set:', properties);
    } catch (error) {
      console.error('[GA4] Error setting user properties:', error);
    }
  }
};

// Export all functions as default object
export default {
  isGtagAvailable,
  trackEvent,
  trackPageView,
  trackFormSubmit,
  trackWhatsAppClick,
  trackPhoneClick,
  trackProductView,
  trackBlogRead,
  trackDownload,
  trackOutboundLink,
  trackSearch,
  trackVideoPlay,
  trackScrollDepth,
  trackTimeOnPage,
  trackError,
  setUserProperties
};
